import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../App';
import DocumentActionPanel from './DocumentActionPanel';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

const CATEGORIES = [
  { key: 'lease', label: 'Leases', desc: 'Executed lease agreements for major tenants' },
  { key: 'estoppel', label: 'Tenant Estoppels', desc: 'Tenant-executed estoppel certificates' },
  { key: 'snda', label: 'SNDAs', desc: 'Subordination, Non-Disturbance and Attornment agreements' },
  { key: 'service_contract', label: 'Service Contracts', desc: 'Property management, maintenance, and vendor contracts' },
  { key: 'other', label: 'Other Diligence', desc: 'Additional borrower-provided diligence items' },
];

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}

function fmtDate(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function UploadZone({ onFiles, disabled }) {
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFiles(files);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current.click()}
      style={{
        border: `2px dashed ${dragOver ? COLORS.gold : COLORS.border}`,
        borderRadius: 6, padding: '14px 20px', textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        background: dragOver ? '#FFF9EC' : COLORS.white,
        transition: 'all 0.15s', opacity: disabled ? 0.5 : 1,
      }}
    >
      <input ref={inputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => {
        const files = Array.from(e.target.files);
        if (files.length) onFiles(files);
        e.target.value = '';
      }} style={{ display: 'none' }} />
      <span style={{ fontSize: 18, color: COLORS.textLight }}>+</span>
      <span style={{ fontSize: 12, color: COLORS.textLight, marginLeft: 8 }}>Drop files or click to upload</span>
    </div>
  );
}

export default function BorrowerDiligence({ deal, onUpdate }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState({});
  const [deleting, setDeleting] = useState(null);
  const token = localStorage.getItem('cmbs_token');

  useEffect(() => { loadDocs(); }, [deal.id]);

  const loadDocs = async () => {
    const res = await fetch(`${API_BASE}/diligence/documents/${deal.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json());
    if (Array.isArray(res)) {
      setDocs(res.filter(d => d.doc_category === 'borrower_diligence'));
    }
  };

  const handleUpload = async (subcategory, files) => {
    setUploading(u => ({ ...u, [subcategory]: true }));
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('deal_id', deal.id);
      fd.append('doc_category', 'borrower_diligence');
      fd.append('doc_subcategory', subcategory);
      try {
        await fetch(`${API_BASE}/diligence/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      } catch (err) {
        alert('Upload failed: ' + err.message);
      }
    }
    await loadDocs();
    setUploading(u => ({ ...u, [subcategory]: false }));
  };

  const handleDelete = async (docId) => {
    setDeleting(docId);
    await fetch(`${API_BASE}/diligence/document/${docId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    await loadDocs();
    setDeleting(null);
  };

  const docsForCategory = (cat) => docs.filter(d => d.doc_type === cat);

  return (
    <div>
      <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 8 }}>Borrower Diligence</h3>

      <div style={{
        background: '#EFF6FF', border: `1px solid #BFDBFE`, borderRadius: 8,
        padding: '12px 16px', marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</span>
        <div>
          <strong style={{ color: '#1D4ED8', fontSize: 13 }}>Lease abstraction and estoppel review coming soon.</strong>
          <p style={{ color: '#1E40AF', fontSize: 12, margin: '4px 0 0' }}>
            Documents are stored and linked to this deal. Use Quick Actions to summarize or extract key details from any uploaded document today.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {CATEGORIES.map(cat => {
          const catDocs = docsForCategory(cat.key);
          return (
            <div key={cat.key} style={{ background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 4 }}>{cat.label}</h4>
                <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>{cat.desc}</p>
              </div>

              <UploadZone onFiles={(files) => handleUpload(cat.key, files)} disabled={uploading[cat.key]} />
              {uploading[cat.key] && <p style={{ fontSize: 12, color: COLORS.textLight, marginTop: 8 }}>Uploading...</p>}

              {catDocs.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  {catDocs.map(doc => (
                    <DocumentActionPanel
                      key={doc.id}
                      doc={doc}
                      deal={deal}
                      token={token}
                      onRefresh={loadDocs}
                      isAppraisal={false}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
